// Page de retour du fournisseur OIDC (/auth/callback).
// Finalise l'échange du code, popule le store utilisateur puis redirige.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { exchangeCode, joinBpmLeagueAndRedirect } from '../../services/oidc/callback';
import { useUserStore } from '../../lib/userStore';
import { getEmbedAwarePath } from '../../lib/embedStore';

export function OidcCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUsername = useUserStore((state) => state.setUsername);
  const setUserId = useUserStore((state) => state.setUserId);
  const setBpmSession = useUserStore((state) => state.setBpmSession);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Guard contre la double exécution du useEffect (StrictMode) : le code est à usage unique
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const run = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const providerError = searchParams.get('error');

      // Le provider peut retourner ?error=access_denied si l'utilisateur annule
      if (providerError) {
        const description = searchParams.get('error_description') || providerError;
        toast.error(`Connexion refusée par le fournisseur : ${description}`);
        navigate(getEmbedAwarePath('/auth'), { replace: true });
        return;
      }

      if (!code || !state) {
        toast.error('Paramètres de connexion manquants');
        navigate(getEmbedAwarePath('/auth'), { replace: true });
        return;
      }

      try {
        const user = await exchangeCode(code, state);
        setUsername(user.username);
        setUserId(user.user_id);
        setBpmSession(true);
        toast.success(`Bienvenue, ${user.username} !`);
        await joinBpmLeagueAndRedirect(user.user_id, navigate);
      } catch (error) {
        console.error('OIDC callback error:', error);
        const message =
          error instanceof Error ? error.message : 'Erreur lors de la connexion';
        setErrorMessage(message);
        toast.error(message);
        navigate(getEmbedAwarePath('/auth'), { replace: true });
      }
    };

    run();
  }, [searchParams, navigate, setUsername, setUserId]);

  return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 gap-3">
      {errorMessage ? (
        <p className="text-danger">{errorMessage}</p>
      ) : (
        <>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Connexion en cours…</span>
          </div>
          <p className="text-muted">Connexion en cours…</p>
        </>
      )}
    </div>
  );
}
